import type { RelistRunMarketplaceInboxStatus } from "@nova-ai/shared"
import type { useRelistSettings } from "../../hooks/use-relist-settings"
import { formatDateTime, formatRelativeTime } from "../../lib/format-date"
import { humanizeMarketplaceId } from "../../lib/marketplace"
import { cn } from "../../lib/utils"
import { Button } from "../ui/button"
import { Switch } from "../ui/switch"
import { Table, type TableColumn } from "../ui/table"

type Settings = ReturnType<typeof useRelistSettings>
type Policy = RelistRunMarketplaceInboxStatus

function PolicyStatus({ policy, saving }: { policy: Policy; saving: boolean }) {
  let label = "Off"
  let tone = "text-muted-foreground"
  if (policy.enabled) {
    label = policy.executionEnabled ? "On" : "Blocked"
    tone = policy.executionEnabled ? "text-success" : "text-warning"
  }
  if (saving) {
    label = "Saving…"
    tone = "text-info"
  }
  return (
    <div>
      <span
        className={cn("inline-flex items-center gap-2 font-medium", tone)}
        role={saving ? "status" : undefined}
      >
        <span
          aria-hidden="true"
          className={cn("size-1.5 rounded-full bg-current", saving && "motion-safe:animate-pulse")}
        />

        {label}
      </span>

      {policy.enabled && !policy.executionEnabled && (
        <p className="mt-1 max-w-64 text-xs text-muted-foreground">
          Relisting is switched off for this deployment. Changes queue until it is re-enabled.
        </p>
      )}
    </div>
  )
}

function WaitingChanges({ policy }: { policy: Policy }) {
  const waiting = policy.pending + policy.claimed
  let meaning = "No queued targets"
  if (waiting > 0) {
    meaning = "Waiting until turned on"
    if (policy.enabled) {
      meaning = policy.executionEnabled ? "Waiting for processing" : "Queued until unblocked"
    }
  }
  if (policy.activationEventId === null && waiting === 0) {
    return (
      <div>
        <span className="text-muted-foreground">Not tracked yet</span>

        <p className="mt-1 text-xs text-muted-foreground">Tracking starts when turned on</p>

        <BlockedTargets policy={policy} />
      </div>
    )
  }
  return (
    <div>
      <span
        className={cn(
          "font-medium tabular-nums",
          waiting > 0 && !policy.effectiveEnabled && "text-warning"
        )}
      >
        {waiting.toLocaleString()}
      </span>

      <p className="mt-1 text-xs text-muted-foreground">{meaning}</p>

      <BlockedTargets policy={policy} />
    </div>
  )
}

function BlockedTargets({ policy }: { policy: Policy }) {
  if (!policy.blocked) {
    return null
  }
  return (
    <div className="mt-1 text-xs text-muted-foreground">
      <p>{`${policy.blocked.toLocaleString()} blocked ${policy.blocked === 1 ? "target" : "targets"}`}</p>

      <p>Inspect recorded blockers with Nova’s relist-queue command.</p>
    </div>
  )
}

export function RelistPolicyTable({ settings, canEdit }: { settings: Settings; canEdit: boolean }) {
  // Background reads may publish before this write has been confirmed.
  const { unconfirmedPreference } = settings
  const columns: TableColumn<Policy>[] = [
    {
      key: "marketplace",
      header: "Marketplace",
      render: (policy) => (
        <div>
          <span className={cn("font-medium", !policy.enabled && "text-muted-foreground")}>
            {humanizeMarketplaceId(policy.marketplace)}
          </span>

          <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
            {policy.marketplace}
          </p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (policy) => (
        <PolicyStatus
          policy={policy}
          saving={
            settings.save.isPending &&
            settings.save.variables?.policy.marketplace === policy.marketplace
          }
        />
      ),
    },
    {
      key: "waiting",
      header: "Waiting changes",
      headerHint: "Known pending and claimed targets. Unread source events are not included.",
      render: (policy) => <WaitingChanges policy={policy} />,
    },
    {
      key: "last-run",
      header: "Last relist run",
      className: "hidden lg:table-cell",
      headerHint: "When changes last entered a relist run; this does not mean it was submitted.",
      render: (policy) =>
        policy.lastEnrolledAt ? (
          <time
            dateTime={policy.lastEnrolledAt}
            title={formatDateTime(policy.lastEnrolledAt)}
            className="whitespace-nowrap tabular-nums text-muted-foreground"
          >
            {formatRelativeTime(policy.lastEnrolledAt)}
          </time>
        ) : (
          <span className="text-muted-foreground">Never</span>
        ),
    },
    {
      key: "toggle",
      header: "Auto relist",
      align: "right",
      render: (policy) => (
        <Switch
          id={`relist-${policy.marketplace}`}
          label={`Auto relist for ${humanizeMarketplaceId(policy.marketplace)}`}
          checked={
            unconfirmedPreference?.marketplace === policy.marketplace
              ? unconfirmedPreference.enabled
              : policy.enabled
          }
          disabled={
            !canEdit || settings.save.isPending || settings.needsRefresh || settings.inbox.isError
          }
          generousTarget
          onCheckedChange={(enabled) => settings.change(policy, enabled)}
        />
      ),
    },
  ]
  return (
    <Table
      columns={columns}
      rows={settings.inbox.data?.marketplaces ?? []}
      rowKey={(row) => row.marketplace}
      className="relist-policy-table"
      cellAlign="middle"
      rowDetail={(policy) =>
        settings.errors[policy.marketplace] ? (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 bg-destructive/5 px-5 py-3 text-sm"
          >
            <p className="min-w-0 flex-1 text-destructive">{settings.errors[policy.marketplace]}</p>

            <Button size="sm" variant="ghost" onClick={() => settings.dismiss(policy.marketplace)}>
              Dismiss
            </Button>
          </div>
        ) : null
      }
    />
  )
}
